import { ChainInfo } from 'api/v1/chains'
import SelectTokenAndNetwork from 'components/SelectTokenAndNetwork'
import toastify from 'components/Toastify'
import { BigNumber } from 'ethers'
import { isAddress } from 'ethers/lib/utils'
import { atom, useAtom } from 'jotai'
import { FC, memo, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import {
  updateAmount,
  updateSelectFast,
  updateShowAddressBookList,
  updateWithdrawing,
} from 'store/withdraw/actions'
import {
  useAmount,
  useSelectedToken,
  useSelectFast,
  useShowAddAddress,
  useWithdrawAddress,
  useWithdrawLayoutType,
} from 'store/withdraw/hook'
import { WithdrawLayoutType } from 'store/withdraw/types'
import { Space12, Space16, Space24, Space4, Space8 } from 'styles'
import { useOnClickOutside } from 'usehooks-ts'
import { computeReceiveAmount } from 'utils/amount'
import { fastWithdrawFeeRatioToAmount } from 'utils/fastWithdrawFee'
import { gaEvent, GaEventName } from 'utils/ga'
import { bn, e2w, parseEtherToWei } from 'utils/number'
import WithdrawAddAddress from './WithdrawAddAddress'
import WithdrawAddressBookListFade from './WithdrawAddressBookList'
import WithdrawAddressInput from './WithdrawAddressInput'
import WithdrawAmountInput from './WithdrawAmountInput'
import { WithdrawButton } from './WithdrawButton'
import WithdrawReceive from './WithdrawReceive'
import WithdrawSpeed from './WithdrawSpeed'
import WithdrawTokenAndNetwork from './WithdrawTokenAndNetwork'
import { FromTip, WithdrawSelectWrap } from './style'
import { useTransfer } from '../../hooks/zkex/useTransfer'
import { useTransferFee } from '../../hooks/zkex/useTransferFee'
import { useWithdrawFee } from '../../hooks/zkex/useWithdrawFee'
import { useWithdraw } from '../../hooks/zkex/useWithdraw'
import { useSpotTradeAvailableBalance } from '../../hooks/spot/useSpotTradeAvailableBalance'
import { MainnetChainIds } from '../../constants/chains'
import { fastWithdrawTieredRates } from '../../utils/fastWithdrawFee'
import { useBrokerInfo } from '../../store/broker/hooks'
import { WithdrawBroker } from './WithdrawBroker'
import { usePerpPositions } from 'store/perp/hooks'
import AccountImpact from './AccountImpact'

export const withdrawSelectNet = atom<ChainInfo | undefined>(undefined)

const WithdrawMain: FC<{
  myAddress: string
  fetchAddressBookList: () => void
}> = memo(({ fetchAddressBookList, myAddress }) => {
  const layoutType = useWithdrawLayoutType()
  const showAddAddress = useShowAddAddress()
  const amount = useAmount()
  const address = useWithdrawAddress()
  const selectedToken = useSelectedToken()
  const selectFast = useSelectFast()
  const [selectedNetwork, setSelectedNetwork] = useAtom(withdrawSelectNet)
  const positions = usePerpPositions()

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const withdraw = useWithdraw()
  const transfer = useTransfer()
  const available = useSpotTradeAvailableBalance(selectedToken?.name!)
  const AddressBookIconRef = useRef<any>()

  const brokerInfo = useBrokerInfo()
  const { fee: maxBrokerFee, limit: brokerLimit } = brokerInfo ?? {}

  const brokerTieredFeeRates = useMemo(() => {
    if (selectedNetwork?.layerOneChainId === MainnetChainIds.Arbitrum) {
      return 100
    }
    return fastWithdrawTieredRates(
      e2w(amount),
      bn(brokerLimit ?? '0'),
      maxBrokerFee ?? 0,
    )
  }, [amount, maxBrokerFee, brokerLimit, selectedNetwork?.layerOneChainId])

  useEffect(() => {
    fetchAddressBookList()
  }, [])

  const selectedTokenChain = useMemo(() => {
    if (selectedNetwork?.layerTwoChainId) {
      return selectedToken?.chains?.[selectedNetwork.layerTwoChainId]
    }
    return undefined
  }, [selectedToken, selectedNetwork])

  useEffect(() => {
    dispatch(updateSelectFast(!!selectedTokenChain?.fastWithdraw))
  }, [dispatch, selectedTokenChain])

  useOnClickOutside(AddressBookIconRef, () =>
    dispatch(updateShowAddressBookList(false)),
  )

  const [withdrawFee, withdrawFeePending] = useWithdrawFee({
    address: address!,
    currency: selectedToken?.name!,
    chainId: selectedNetwork?.layerTwoChainId!,
    fastWithdraw: selectFast ? 1 : 0,
  })
  const [transferFee, transferFeePending] = useTransferFee({
    address: address!,
    currency: selectedToken?.name!,
  })

  const fee =
    layoutType === WithdrawLayoutType.Withdraw ? withdrawFee : transferFee

  const canWithdrawAmount = useMemo(() => {
    if (!fee) {
      return BigNumber.from('0')
    }
    return computeReceiveAmount(parseEtherToWei(amount), fee, bn(available))
  }, [amount, available, fee])

  const fastFee = useMemo(() => {
    if (!canWithdrawAmount || canWithdrawAmount.isZero()) {
      return bn('0')
    }
    return fastWithdrawFeeRatioToAmount(canWithdrawAmount, brokerTieredFeeRates)
  }, [canWithdrawAmount, brokerTieredFeeRates])

  const receiveAmount: BigNumber | undefined = useMemo(() => {
    if (layoutType === WithdrawLayoutType.Withdraw && selectFast) {
      return canWithdrawAmount?.sub(fastFee)
    } else {
      return canWithdrawAmount
    }
  }, [canWithdrawAmount, fastFee, selectFast, layoutType])

  const sendWithdraw = async () => {
    // layerNum = 1  to layer1
    // layerNum = 2  to layer2
    if (!address) {
      toastify.warn(t('common-enter-address'))
      return
    }
    if (!isAddress(address)) {
      toastify.warn(t('common-wrong-address'))
      return
    }
    if (!Number(amount)) {
      toastify.warn(t('common-enter-amount'))
      return
    }

    if (!canWithdrawAmount) {
      toastify.warn('Not enough balance to withdraw.')
      return
    }

    if (fee === undefined) {
      toastify.warn('Failed to get withdrawal fee, please try again later.')
      return
    }

    if (!receiveAmount || receiveAmount.lte(0)) {
      toastify.warn('Not enough balance to cover the withdrawal fee.')
      return
    }

    if (layoutType === WithdrawLayoutType.Withdraw) {
      withdraw({
        address,
        layerOneChainId: selectedNetwork?.layerOneChainId!,
        tokenId: selectedToken?.l2CurrencyId!,
        tokenSymbol: selectedToken?.l2Symbol!,
        amount: canWithdrawAmount,
        fastWithdraw: selectFast ? 1 : 0,
        withdrawFeeRatio: selectFast ? brokerTieredFeeRates : 0,
        fee,
      })
        .then(() => {
          dispatch(updateAmount(''))
        })
        .finally(() => {
          dispatch(updateWithdrawing(false))
        })

      gaEvent(GaEventName.request_withdraw, {
        symbol: selectedToken?.name!,
      })
    } else if (layoutType === WithdrawLayoutType.Transfer) {
      transfer({
        address,
        tokenId: selectedToken?.l2CurrencyId!,
        tokenSymbol: selectedToken?.l2Symbol!,
        amount: canWithdrawAmount,
        fee,
      })
        .then(() => {
          dispatch(updateAmount(''))
        })
        .finally(() => {
          dispatch(updateWithdrawing(false))
        })
    }
  }
  return (
    <WithdrawSelectWrap>
      <FromTip>{t('withdraw-destination')}</FromTip>
      <Space8 />
      {/* address input */}
      <WithdrawAddressInput
        myAddress={myAddress}
        AddressBookIconRef={AddressBookIconRef}
      />
      {/* fade Address List  */}
      <WithdrawAddressBookListFade
        myAddress={myAddress}
        fetchAddressBookList={fetchAddressBookList}
      />

      {/* Tap new address */}
      {showAddAddress && <WithdrawAddAddress />}
      <Space12 />
      <FromTip>Token</FromTip>
      <Space8 />
      {/* select token and select network */}
      {layoutType === WithdrawLayoutType.Transfer ? (
        <WithdrawTokenAndNetwork
          selectedNetwork={selectedNetwork}
          setSelectedNetwork={setSelectedNetwork}
        />
      ) : (
        <SelectTokenAndNetwork
          showSupportFast={true}
          selectedNetwork={selectedNetwork}
          setSelectedNetwork={setSelectedNetwork}
        />
      )}

      <Space12 />
      {/* amount input */}
      <WithdrawAmountInput
        l2Balance={available}
        selectedNetwork={selectedNetwork}
      />

      {positions.length > 0 && (
        <>
          <Space4 />
          <AccountImpact />
        </>
      )}

      {/* Withdrawal Speed Options */}
      {layoutType === WithdrawLayoutType.Withdraw && (
        <WithdrawSpeed selectedNetwork={selectedNetwork} />
      )}

      <Space8 />
      {/* Receive  */}
      <WithdrawReceive
        chainId={selectedNetwork?.layerTwoChainId}
        receiveAmount={receiveAmount}
        fastFee={fastFee} // Fast withdrawal fee
        withdrawalFee={fee} // Standard withdrawal fee
        pending={transferFeePending || withdrawFeePending}
      />
      <Space12 />
      {/* withdraw button */}
      <WithdrawButton
        l2Balance={available}
        fee={fee}
        brokerLimit={brokerLimit}
        sendWithdraw={sendWithdraw}
        selectedNetwork={selectedNetwork}
      />
      <WithdrawBroker />
    </WithdrawSelectWrap>
  )
})
export default WithdrawMain
